import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from './components/Header';
import { HeroSection } from './sections/HeroSection';
import { ProgressiveTextSection } from './sections/ProgressiveTextSection';
import { AboutSection } from './sections/AboutSection';
import { FeaturedProjectSection } from './sections/FeaturedProjectSection';
import { ProjectsGrid } from './sections/ProjectsGrid';
import { ParallaxCaseStudies } from './sections/ParallaxCaseStudies';
import { ExpertiseSection } from './sections/ExpertiseSection';
import { MetricsSection } from './sections/MetricsSection';
import { TestimonialsSection } from './sections/TestimonialsSection';
import { CTASection } from './sections/CTASection';
import { Footer } from './components/Footer';
import { CustomCursor } from './components/CustomCursor';
import { ScrollProvider } from './context/ScrollContext';
import { FloatingButtons } from './components/FloatingButtons';
import { ChatDrawer } from './components/ChatDrawer';
import { AnimationProvider } from './utils/AnimationObserver';
import { usePortfolio } from './context/PortfolioContext';
import { SEO } from './components/SEO';
import { StructuredData } from './components/StructuredData';
import './styles/animations.css';
export function App() {
  const {
    setViewPreference,
    isChatDrawerOpen
  } = usePortfolio();
  // Set scroll restoration to manual on app mount
  useEffect(() => {
    // Use history API to ensure scroll restoration is manual
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  // Set view preference to 'traditional' when this page loads - ONLY ONCE
  useEffect(() => {
    setViewPreference('traditional');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount
  // Dummy handler for ChatDrawer prop (not used anymore since we navigate directly)
  const handleCaseStudyClick = (slug: string) => {
    // This is now handled inside ChatDrawer via navigate()
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  return <>
      <SEO title="Home - Mateo Cohen" description="Lead AI Product Designer specializing in enterprise UX, design systems, and AI innovation. 8+ years experience in financial services, cybersecurity, and conversational AI design." url="https://mateocohen.com" type="website" data-id="element-0" data-mp="3uf5gljh6s977" />
      <StructuredData type="portfolio" data-id="element-1" data-mp="3glnxu0wbr0i8" />
      <ScrollProvider data-id="element-2" data-mp="3bmjs5k8lzkwn">
        <AnimationProvider data-id="element-3" data-mp="3bncpxvplb02n">
          <div className="w-full min-h-screen bg-black text-white" data-id="element-4" data-mp="3bo4lx7n3mztg">
            <CustomCursor data-id="element-5" data-mp="3qc96yi9mjnfe" />

            {/* Flex container: Main content + Drawer side-by-side */}
            <div className="flex min-h-screen" data-id="element-6" data-mp="3ljg1tta8ud2s">
              {/* Main Content Area - Takes remaining space */}
              <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-7" data-mp="2xtlolam0imi0">
                <Header data-id="element-8" data-mp="2swbpt4cqgyhi" />

                {/* Portfolio Content */}
                <motion.div initial={{
                opacity: 1
              }} className="flex-1" data-id="element-9" data-mp="2stzakyyd1285">
                  <HeroSection data-id="element-10" data-mp="1d4hmf2ij765c" />
                  <ParallaxCaseStudies data-id="element-11" data-mp="1d4rd6sbehhc3" />
                  <ExpertiseSection data-id="element-12" data-mp="1d5suhej0f029" />
                  <TestimonialsSection data-id="element-13" data-mp="yfv6ozozscdl" />
                  <AboutSection data-id="element-14" data-mp="yh6d779tny8e" />
                  <MetricsSection data-id="element-15" data-mp="ydqwy14ewj5e" />
                  <CTASection data-id="element-16" data-mp="13dwsvthpsrta" />
                  <Footer data-id="element-17" data-mp="13f7tjkfv5jg6" />
                </motion.div>
              </div>

              {/* Chat Drawer - Fixed width column on desktop, hidden on mobile */}
              <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-18" data-mp="1w3vlfyv4b72p" />
            </div>

            {/* Floating button - only show when drawer is CLOSED */}
            {!isChatDrawerOpen && <FloatingButtons data-id="element-19" data-mp="1m560fx7xd6i8" />}
          </div>
        </AnimationProvider>
      </ScrollProvider>
    </>;
}